import { isEqual } from 'lodash';
import React, { memo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

import { useLocation } from 'react-router';
import isFunction from 'utils/isFunction';
import { arrayStartsWith, findSubArray } from 'utils/array';
import { BreadcrumbsDispatchContext, BreadcrumbsStateContext } from './Context';
import getBreadcrumbs from './getBreadcrumbs';

const EXPECTED_ADJUSTMENTS = [
  { path: ['specifications', 'folders'], adjustKeys: ['specificationFolder'] },
  { path: ['specifications', 'details'], adjustKeys: ['specification'] },
];

const findNamesSubArray = (allNames, namesToMatch) => {
  // jeśli podano undefined jako któreś nameToMatch, chcemy zmatchować w tym miejscu dowolne breadcrumbName
  const comparator = (breadcrumbName, nameToMatch) =>
    typeof nameToMatch === 'undefined' ? true : isEqual(breadcrumbName, nameToMatch);

  return findSubArray(allNames, namesToMatch, comparator);
};

const shouldAdjust = (breadcrumbs, { matchBreadcrumbNames }) => {
  const allNames = breadcrumbs.map((b) => b.name);
  const partNamesIndex = findNamesSubArray(allNames, matchBreadcrumbNames);
  return partNamesIndex !== null;
};

const doAdjust = (breadcrumbs, { matchBreadcrumbNames, replaceBreadcrumbs }) => {
  const allNames = breadcrumbs.map((b) => b.name);
  const partNamesIndex = findNamesSubArray(allNames, matchBreadcrumbNames);

  const beginning = breadcrumbs.slice(0, partNamesIndex);
  const oldPart = breadcrumbs.slice(partNamesIndex, partNamesIndex + matchBreadcrumbNames.length);
  const ending = breadcrumbs.slice(
    partNamesIndex + matchBreadcrumbNames.length,
    breadcrumbs.length
  );

  const newPart = replaceBreadcrumbs(oldPart);

  return [...beginning, ...newPart, ...ending];
};

const reducer =
  ({ commonReplace, commonRemove, pathname, t }) =>
  (prev, { payload, action }) => {
    const replace = { ...prev?.replace, ...(payload?.replace || {}) };
    const adjust = { ...prev?.adjust, ...(payload?.adjust || {}) };
    const remove = [...prev?.remove];

    const { breadcrumbs: breadcrumbsFromPath } = getBreadcrumbs(pathname);

    [...(payload?.remove || []), ...commonRemove].forEach((element) => {
      if (!remove.includes(element)) {
        remove.push(element);
      }
    });

    const breadcrumbsAfterReplaceRemove = breadcrumbsFromPath.reduce((bc, curr) => {
      const replaceKeys = [
        ...new Set([
          ...Object.keys(isFunction(commonReplace) ? commonReplace(t) : commonReplace),
          ...Object.keys(replace),
        ]),
      ];
      if (replaceKeys.includes(curr?.name)) {
        return [
          ...bc,
          {
            name: curr?.name,
            label:
              replace[curr?.name] ||
              (isFunction(commonReplace)
                ? commonReplace(t)[curr?.name]
                : commonReplace[curr?.name]),
            url: curr?.url,
          },
        ];
      }
      if (remove.includes(curr?.name)) return [...bc];
      return [...bc, curr];
    }, []);

    let adjustedBreadcrumbs;
    if (
      EXPECTED_ADJUSTMENTS.some(
        (expectedAdjustment) =>
          arrayStartsWith(
            breadcrumbsFromPath.map((b) => b.name),
            expectedAdjustment.path
          ) &&
          !expectedAdjustment.adjustKeys.every(
            (key) => adjust[key] && shouldAdjust(breadcrumbsAfterReplaceRemove, adjust[key])
          )
      )
    ) {
      adjustedBreadcrumbs = [];
    } else {
      adjustedBreadcrumbs = breadcrumbsAfterReplaceRemove;
      Object.values(adjust).forEach((adjustConfig) => {
        if (shouldAdjust(adjustedBreadcrumbs, adjustConfig)) {
          adjustedBreadcrumbs = doAdjust(adjustedBreadcrumbs, adjustConfig);
        }
      });
    }

    switch (action?.type) {
      case 'PARAMETERS':
        return {
          remove: [...remove],
          replace: { ...replace },
          adjust: { ...adjust },
          breadcrumbs: adjustedBreadcrumbs,
        };
      default:
        break;
    }
  };

const BreadcrumbsProvider = ({ commonReplace = {}, commonRemove = [], children }) => {
  const { t } = useTranslation(['common', 'specification']);
  const location = useLocation();
  const { pathname } = location;
  const { breadcrumbs } = getBreadcrumbs(pathname);
  const [state, dispatch] = useReducer(reducer({ commonReplace, commonRemove, pathname, t }), {
    replace: {},
    adjust: {},
    remove: [],
    breadcrumbs,
  });

  return (
    <BreadcrumbsDispatchContext.Provider value={dispatch}>
      <BreadcrumbsStateContext.Provider value={state}>{children}</BreadcrumbsStateContext.Provider>
    </BreadcrumbsDispatchContext.Provider>
  );
};

export default memo(BreadcrumbsProvider);
